import { pollUntil } from "./pollUntil";
import type {
  CmmdClaudeRuntimeReadiness,
  ProviderKind,
  ServerProvider,
} from "@cmmd-forge/contracts";
import { isServerProviderExecutable } from "@cmmd-forge/shared/providerExecution";
import {
  resolveProviderEligibility,
  type ForgeProviderAccessState,
  type ProviderCredentialMode,
} from "../cmmd/forgeProviderAccess";

// A runtime socket can be healthy before provider CLI discovery publishes its
// first executable inventory. Keep New Thread and Task Start work on the same
// bounded live-state wait: fast in tests, 20 seconds in production.
const PROVIDER_INVENTORY_POLL_INTERVAL_MS = import.meta.env.MODE === "test" ? 20 : 500;
const PROVIDER_INVENTORY_MAX_ATTEMPTS = import.meta.env.MODE === "test" ? 10 : 40;

// Environment, not runtime, in every sentence a person reads: runtime is the
// word the API uses on the wire. See `CONTEXT.md`, "Cloud execution".
export const NO_AUTHORIZED_PROVIDER_MESSAGE =
  "No provider is both authorized for your account and ready on this Environment. Connect an available provider in Settings, then try again.";
const PROVIDER_INVENTORY_TIMEOUT_MESSAGE =
  "This project is taking longer than usual to publish its provider inventory. Wait a moment and try again.";
const CLAUDE_NATIVE_READINESS_TIMEOUT_MESSAGE =
  "Claude is connected, but its native runtime credential is still being applied. Wait a moment and try again.";
const CLAUDE_NATIVE_READINESS_FAILED_MESSAGE =
  "Claude's native runtime credential could not be applied. Reconnect Claude in Settings, then try again.";
const CLAUDE_NATIVE_READINESS_STALE_MESSAGE =
  "Claude's native runtime credential is no longer valid for this Environment. Reconnect Claude in Settings, then try again.";
const CLAUDE_NATIVE_READINESS_UNAVAILABLE_MESSAGE =
  "Forge could not verify Claude's native runtime credential for this Environment. Wait a moment, then reconnect Claude in Settings if the issue continues.";
// CMMD-36473: the Environment itself cannot take a delivery. Read before every
// status-based sentence, because the answer keeps the row's real status and
// that status may still say pending.
const CLAUDE_ENVIRONMENT_NOT_RUNNING_MESSAGE =
  "This Environment is not running, so Claude cannot run on it. Restart the Environment, then try again.";
const RUNTIME_UNAVAILABLE_READINESS_REASON = "runtime_unavailable";
const CODEX_RECONNECT_REQUIRED_MESSAGE =
  "Your saved ChatGPT sign-in expired. Reconnect ChatGPT in Settings, then try again.";
const CLAUDE_NATIVE_READINESS_IN_PROGRESS_STATUSES = new Set<
  CmmdClaudeRuntimeReadiness["deliveryStatus"]
>(["pending", "claimed", "activating"]);

type ReadyForgeProviderAccess = Extract<ForgeProviderAccessState, { readonly status: "ready" }>;

/** Polls the complete, live provider-authority tuple with a finite budget. */
export async function waitForAuthorizedProviderLaunch<T>(input: {
  readonly loadProviderAccess: () => Promise<ReadyForgeProviderAccess>;
  readonly readProviders: () => readonly ServerProvider[] | null;
  readonly credentialMode: ProviderCredentialMode;
  readonly pinnedProvider: ProviderKind | null;
  readonly resolve: (attempt: {
    readonly providerAccess: ReadyForgeProviderAccess;
    readonly providers: readonly ServerProvider[];
  }) => T | null;
}): Promise<T> {
  let latestProviders: readonly ServerProvider[] | null = null;
  let latestProviderAccess: ReadyForgeProviderAccess | null = null;
  const launch = await pollUntil(
    async () => {
      const providerAccess = await input.loadProviderAccess();
      latestProviderAccess = providerAccess;
      latestProviders = input.readProviders();
      const attempt = {
        providerAccess,
        providers: latestProviders ?? [],
      };
      const resolved = input.resolve(attempt);
      if (resolved !== null) return resolved;

      const terminalError = resolveTerminalProviderLaunchReadinessError({
        ...attempt,
        credentialMode: input.credentialMode,
        pinnedProvider: input.pinnedProvider,
      });
      if (terminalError !== null) throw new Error(terminalError);
      return null;
    },
    {
      maxAttempts: PROVIDER_INVENTORY_MAX_ATTEMPTS,
      intervalMs: PROVIDER_INVENTORY_POLL_INTERVAL_MS,
    },
  );
  if (launch !== null) return launch;

  latestProviders = input.readProviders();
  throw new Error(
    resolveProviderLaunchReadinessError({
      providers: latestProviders,
      providerAccess: latestProviderAccess,
      credentialMode: input.credentialMode,
      pinnedProvider: input.pinnedProvider,
    }),
  );
}

function resolveTerminalProviderLaunchReadinessError(input: {
  readonly providers: readonly ServerProvider[];
  readonly providerAccess: ReadyForgeProviderAccess;
  readonly credentialMode: ProviderCredentialMode;
  readonly pinnedProvider: ProviderKind | null;
}): string | null {
  if (
    input.pinnedProvider === "codex" &&
    input.providerAccess.personalReconnectRequired?.codex === true
  ) {
    return CODEX_RECONNECT_REQUIRED_MESSAGE;
  }
  // Only an explicit Claude selection may fail immediately. Unpinned launches
  // keep their bounded inventory wait because provider snapshots are
  // incremental and another authorized provider can still appear next.
  if (input.pinnedProvider !== "claudeAgent") return null;
  const readiness = input.providerAccess.claudeRuntimeReadiness;
  if (!isTerminalClaudeNativeReadiness(readiness)) return null;
  const hasClaudeNativeReadinessFailure = input.providers.some(
    (provider) =>
      provider.provider === "claudeAgent" &&
      hasNativeRuntimeReadinessFailure(provider, input.providerAccess, input.credentialMode),
  );
  return hasClaudeNativeReadinessFailure ? describeClaudeNativeReadinessFailure(readiness) : null;
}

function isTerminalClaudeNativeReadiness(
  readiness: CmmdClaudeRuntimeReadiness | null,
): readiness is CmmdClaudeRuntimeReadiness {
  return (
    readiness !== null &&
    (isRuntimeUnavailable(readiness) ||
      !readiness.connected ||
      readiness.deliveryStatus === "not_connected" ||
      readiness.deliveryStatus === "failed" ||
      readiness.deliveryStatus === "stale")
  );
}

function shouldDescribeClaudeNativeReadiness(pinnedProvider: ProviderKind | null): boolean {
  return pinnedProvider === null || pinnedProvider === "claudeAgent";
}

function resolveProviderLaunchReadinessError(input: {
  readonly providers: readonly ServerProvider[] | null;
  readonly providerAccess: ReadyForgeProviderAccess | null;
  readonly credentialMode: ProviderCredentialMode;
  readonly pinnedProvider: ProviderKind | null;
}): string {
  if (
    shouldDescribeClaudeNativeReadiness(input.pinnedProvider) &&
    input.providerAccess !== null &&
    input.providers !== null
  ) {
    const hasClaudeNativeReadinessFailure = input.providers.some(
      (provider) =>
        provider.provider === "claudeAgent" &&
        hasNativeRuntimeReadinessFailure(
          provider,
          input.providerAccess as ReadyForgeProviderAccess,
          input.credentialMode,
        ),
    );
    if (hasClaudeNativeReadinessFailure) {
      return describeClaudeNativeReadinessFailure(input.providerAccess.claudeRuntimeReadiness);
    }
  }
  return resolveProviderInventoryReadinessError(input.providers);
}

function describeClaudeNativeReadinessFailure(
  readiness: CmmdClaudeRuntimeReadiness | null,
): string {
  if (readiness === null) return CLAUDE_NATIVE_READINESS_UNAVAILABLE_MESSAGE;
  if (isRuntimeUnavailable(readiness)) return CLAUDE_ENVIRONMENT_NOT_RUNNING_MESSAGE;
  if (!readiness.connected || readiness.deliveryStatus === "not_connected") {
    return CLAUDE_NATIVE_READINESS_STALE_MESSAGE;
  }
  if (CLAUDE_NATIVE_READINESS_IN_PROGRESS_STATUSES.has(readiness.deliveryStatus)) {
    return CLAUDE_NATIVE_READINESS_TIMEOUT_MESSAGE;
  }
  if (readiness.deliveryStatus === "failed") return CLAUDE_NATIVE_READINESS_FAILED_MESSAGE;
  if (readiness.deliveryStatus === "stale") return CLAUDE_NATIVE_READINESS_STALE_MESSAGE;
  // An applied row that lacks native executable proof or an exact generation
  // is inconsistent. Never describe it as an in-progress delivery.
  return CLAUDE_NATIVE_READINESS_UNAVAILABLE_MESSAGE;
}

function hasNativeRuntimeReadinessFailure(
  provider: ServerProvider,
  providerAccess: ReadyForgeProviderAccess,
  credentialMode: ProviderCredentialMode,
): boolean {
  const eligibility = resolveProviderEligibility({
    provider,
    providerKind: provider.provider,
    access: providerAccess,
    credentialMode,
  });
  return (
    !eligibility.eligible &&
    (eligibility.reason === "native-runtime-not-ready" ||
      eligibility.reason === "environment-not-running")
  );
}

function isRuntimeUnavailable(readiness: CmmdClaudeRuntimeReadiness): boolean {
  return readiness.reason === RUNTIME_UNAVAILABLE_READINESS_REASON;
}

export function resolveProviderInventoryReadinessError(
  providers: readonly ServerProvider[] | null,
): string {
  return providers?.some(isServerProviderExecutable) === true
    ? NO_AUTHORIZED_PROVIDER_MESSAGE
    : PROVIDER_INVENTORY_TIMEOUT_MESSAGE;
}
